import { TenFourText } from '@/components/TenFourText'
import type { ReactNode } from 'react'
import { ChromeIcon } from '@/components/ChromeIcon'
import { QueueIcon, queueIconColor } from '@/components/QueueIcon'
import { PageHeaderIcon } from '@/components/layout/PageHeaderIcon'
import { ErrorState } from '@/components/feedback/ErrorState'
import { useCustomer } from '@/modules/customers/hooks'
import { useJob } from '@/modules/jobs/hooks'
import { useInvoice } from '@/modules/invoices/hooks'
import { formatCurrency, formatDate, formatPhone, getStatusLabel } from '@/lib/utils'
import type { Customer, JobUrgency } from '@/lib/types'
import { statusLabel as jobStatusLabel, typeLabel as jobTypeLabel } from '@/pages/authenticated/jobs/utils'
import type { FlatItem } from '@/hooks/useGlobalSearch'
import { HomeClayIcon } from '@/pages/authenticated/home/HomeClayIcon'
import { ConversationAvatar } from '@/pages/authenticated/conversations/ConversationAvatar'
import type { QuickAction, QuickActionMark } from './actions'
import styles from './GlobalSearchDialog.module.css'
import { formatCustomerNo, formatInvoiceNo } from '@tenfour/contracts'

const URGENCY_LABELS: Record<JobUrgency, string> = { LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High', EMERGENCY: 'Emergency' }

/** Destination mark in a 44px (or 64px) slot. Settings pages resolve to the
 *  rail's colour chip, which centres in the slot instead of filling it. */
export function PageMark({ pathname, large = false }: { pathname: string; large?: boolean }) {
  return (
    <span className={`${large ? styles.largeMark : styles.mark} ${styles.markSlot}`}>
      <PageHeaderIcon pathname={pathname} className={styles.markFill} />
    </span>
  )
}

/** A quick action's mark: entity clay, chrome relief, or destination art. */
export function ActionMark({ mark, large = false }: { mark: QuickActionMark; large?: boolean }) {
  const className = large ? styles.largeMark : styles.mark
  if (mark.kind === 'entity') return <QueueIcon meaning={mark.meaning} color={mark.color} className={className} />
  if (mark.kind === 'chrome') return <ChromeIcon name={mark.name} size={large ? 64 : 44} className={className} />
  return <PageMark pathname={mark.pathname} large={large} />
}

/** The footer's quiet second link beside "Open record ↗": the record's index. */
export function recordDestination(item: FlatItem): { href: string; label: string } | null {
  switch (item.type) {
    case 'customer': return { href: '/customers-v2', label: 'View customers' }
    case 'job': return { href: '/jobs', label: 'View jobs' }
    case 'invoice': return { href: '/invoices', label: 'View invoices' }
    default: return null
  }
}

export function recordOpenLabel(item: FlatItem): string {
  return item.type === 'page' ? 'Open page' : 'Open record'
}

/** One label/value cell in the preview's Details card. Blank values are
 *  dropped rather than rendered as an em dash — an empty cell is noise, not
 *  information. */
function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null
  return (
    <div className={styles.fact}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}

/** Identity (mark, name, "● Kind · Status"), the 2px Details card of fact
 *  cells, then an optional Context paragraph — the design system's preview. */
function PreviewShell({ mark, title, kind, status, loading, error, retry, context, children }: {
  mark: ReactNode; title: string; kind: string; status?: string | null; loading?: boolean
  error?: boolean; retry?: () => void; context?: string | null; children?: ReactNode
}) {
  return <>
    <div className={styles.identity}>
      {mark}
      <div className={styles.identityText}><h2>{title}</h2><p className={styles.entityLabel}>{status ? `${kind} · ${status}` : kind}</p></div>
    </div>
    {error && retry ? <ErrorState compact message="This record couldn’t be loaded." onRetry={retry} />
      : loading ? <p role="status" className={styles.status}>Loading record…</p>
      : <>
        <section className={styles.card}><h3>Details</h3><dl className={styles.facts}>{children}</dl></section>
        {context && <section className={styles.contextSection}><h3>Context</h3><p className={styles.context}>{context}</p></section>}
      </>}
  </>
}

function customerStatus(customer: Customer): string {
  if (customer.status === 'INACTIVE') return 'Inactive'
  if (customer.isVip) return 'VIP'
  return customer.contactStatus === 'CUSTOMER' ? 'Active' : 'Prospect'
}

function CustomerPreview({ item }: { item: FlatItem }) {
  const { data: customer, isLoading, isError, refetch } = useCustomer(item.id)
  return (
    <PreviewShell
      mark={<ConversationAvatar seed={item.id} className={styles.largeMark} />}
      title={customer?.name ?? item.label}
      kind="Customer"
      status={customer ? customerStatus(customer) : undefined}
      loading={isLoading}
      error={isError}
      retry={refetch}
      context={customer?.notes}
    >
      {/* Someone who searched "CUS-0007" should see that id confirmed on the
          record they landed on, not just on the row they clicked (#440). */}
      <Field
        label="Customer ID"
        value={customer?.customerNumber != null ? formatCustomerNo(customer.customerNumber) : null}
      />
      <Field label="Phone" value={customer?.phone ? formatPhone(customer.phone) : null} />
      <Field label="Email" value={customer?.email} />
      <Field label="Address" value={customer?.address} />
      <Field label="Jobs" value={customer ? String(customer.totalJobs) : null} />
      <Field label="Total spent" value={customer ? formatCurrency(customer.totalSpent) : null} />
      <Field label="Last job" value={customer?.lastJobAt ? formatDate(customer.lastJobAt) : null} />
    </PreviewShell>
  )
}

function JobPreview({ item }: { item: FlatItem }) {
  const { data: job, isLoading, isError, refetch } = useJob(item.id)
  return (
    <PreviewShell
      mark={
        <HomeClayIcon meaning="job" seed={item.id} className={styles.largeMark} />
      }
      title={job?.customerName ?? item.label}
      kind="Job"
      status={job ? jobStatusLabel(job.status) : undefined}
      loading={isLoading}
      error={isError}
      retry={refetch}
      context={job?.description}
    >
      <Field label="Type" value={job ? jobTypeLabel(job.type) : null} />
      <Field label="Urgency" value={job ? URGENCY_LABELS[job.urgency] ?? job.urgency : null} />
      <Field label="Phone" value={job?.customerPhone ? formatPhone(job.customerPhone) : null} />
      <Field label="Address" value={job?.customerAddress} />
      <Field label="Assigned" value={job?.assignedUser?.name} />
      <Field label="Created" value={job?.createdAt ? formatDate(job.createdAt) : null} />
    </PreviewShell>
  )
}

function InvoicePreview({ item }: { item: FlatItem }) {
  const { data: invoice, isLoading, isError, refetch } = useInvoice(item.id)
  const quote = invoice?.status === 'QUOTE'
  return (
    <PreviewShell
      mark={<QueueIcon meaning="invoice" color={queueIconColor(item.id)} className={styles.largeMark} />}
      title={invoice ? formatInvoiceNo(invoice.invoiceNumber, { quote }) : item.label}
      kind={quote ? 'Quote' : 'Invoice'}
      status={invoice ? invoice.displayStatus ?? getStatusLabel(invoice.status) : undefined}
      loading={isLoading}
      error={isError}
      retry={refetch}
      context={invoice?.memo}
    >
      <Field label="Customer" value={invoice?.customerName} />
      <Field label="Total" value={invoice ? formatCurrency(invoice.total) : null} />
      <Field
        label="Balance due"
        value={invoice?.balanceDue != null ? formatCurrency(invoice.balanceDue) : null}
      />
      <Field label="Due date" value={invoice?.dueDate ? formatDate(invoice.dueDate) : null} />
      <Field label="Address" value={invoice?.customerAddress} />
      <Field label="Created" value={invoice?.createdAt ? formatDate(invoice.createdAt) : null} />
    </PreviewShell>
  )
}

function PagePreview({ item }: { item: FlatItem }) {
  return <>
    <div className={styles.identity}>
      <PageMark pathname={item.href.split(/[?#]/)[0]} large />
      <div className={styles.identityText}><h2>{item.label}</h2><p className={styles.entityLabel}>Page</p></div>
    </div>
    <section className={styles.card}><h3>About this page</h3><p className={styles.context}><TenFourText>{item.sub}</TenFourText></p></section>
  </>
}

/** The preview of a highlighted quick action: what it does and, when a global
 *  key does the same thing, that shortcut. */
export function ActionPreview({ action, description }: { action: QuickAction; description: string }) {
  return <>
    <div className={styles.identity}>
      <ActionMark mark={action.mark} large />
      <div className={styles.identityText}><h2>{action.label}</h2><p className={styles.entityLabel}>{action.group} · {action.verb}</p></div>
    </div>
    <section className={styles.card}>
      <h3>Details</h3>
      <dl className={styles.facts}>
        <Field label="Does" value={description} />
        <Field label="Shortcut" value={action.shortcut} />
      </dl>
    </section>
  </>
}

/**
 * The dialog's right column: a detail card for the highlighted record. Each
 * branch fetches through the same React Query hook + key the destination page
 * uses (`['customer'|'job'|'invoice', id]`), so arrowing through results warms
 * the cache for the record you are about to open. Pages need no fetch.
 */
export function SearchPreview({ item }: { item: FlatItem | null }) {
  if (!item) {
    return (
      <div className={styles.status}>
        <p>Select a result to preview it.</p>
      </div>
    )
  }

  switch (item.type) {
    case 'customer':
      return <CustomerPreview key={item.id} item={item} />
    case 'job':
      return <JobPreview key={item.id} item={item} />
    case 'invoice':
      return <InvoicePreview key={item.id} item={item} />
    case 'page':
      return <PagePreview key={item.id} item={item} />
  }
}
